import { useState, useRef, useEffect, useCallback } from 'react';

interface UseCardScrollSnapOptions {
  totalCards: number;
  onCardChange?: (index: number) => void;
}

export function useCardScrollSnap({ totalCards, onCardChange }: UseCardScrollSnapOptions) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isScrollingRef = useRef(false);

  const updateCurrentIndex = useCallback((immediate = false) => {
    if (!containerRef.current) return;

    // Allow immediate updates during programmatic scrolling for better responsiveness
    if (!immediate && isScrollingRef.current) return;

    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;

    // Find which card is most visible
    const newIndex = Math.round(scrollTop / containerHeight);
    const clampedIndex = Math.max(0, Math.min(newIndex, totalCards - 1));

    if (clampedIndex !== currentIndex) {
      setCurrentIndex(clampedIndex);
      onCardChange?.(clampedIndex);
    }
  }, [currentIndex, totalCards, onCardChange]);

  const scrollToCard = useCallback((index: number) => {
    if (!containerRef.current || index < 0 || index >= totalCards) return;
    
    isScrollingRef.current = true;
    const container = containerRef.current;
    
    // Use viewport height calculation for consistent positioning
    const targetScrollTop = index * container.clientHeight;
    
    container.scrollTo({
      top: targetScrollTop,
      behavior: 'smooth'
    });
    
    // Reset scrolling flag after animation
    setTimeout(() => {
      isScrollingRef.current = false;
      updateCurrentIndex();
    }, 500);
  }, [totalCards, updateCurrentIndex]);

  // Handle scroll events
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let timeoutId: NodeJS.Timeout;
    let frameId: number;

    const handleScroll = () => {
      // Immediate update using requestAnimationFrame for smooth visual feedback
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        updateCurrentIndex(true); // immediate update
      });

      // Debounced update as fallback for when scrolling stops
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        updateCurrentIndex();
      }, 50); // Reduced from 150ms to 50ms for faster response
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
      cancelAnimationFrame(frameId);
    };
  }, [updateCurrentIndex]);

  // Initialize current index on mount
  useEffect(() => {
    updateCurrentIndex();
  }, [updateCurrentIndex]);

  return {
    currentIndex,
    containerRef,
    cardRefs,
    scrollToCard,
    canGoNext: currentIndex < totalCards - 1,
    canGoPrevious: currentIndex > 0,
  };
}